import {DecimalPipe} from "@angular/common"
import {Component, computed, input, output, signal, ChangeDetectionStrategy} from "@angular/core"
import {SplineCurveComponent} from "@app/common/components/curves/spline-curve/spline-curve.component"
import {Vector2Like} from "@cm/math"
import {computeRgbCurveSampledFunction} from "@cm/material-nodes"

type CurveType = "rgb" | "r" | "g" | "b"

function evaluateRgbCurveOverRange(start: number, end: number, step: number, controlPoints: Vector2Like[]) {
    const sampledFunction = computeRgbCurveSampledFunction(controlPoints)
    const points: Vector2Like[] = []

    for (let x = start; x <= end; x += step) {
        const y = sampledFunction.evaluate(x)
        if (y === undefined) continue

        points.push({x, y})
    }

    return points
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-rgb-curves",
    imports: [SplineCurveComponent, DecimalPipe],
    templateUrl: "./rgb-curves.component.html",
    styleUrl: "./rgb-curves.component.scss",
})
export class RgbCurvesComponent {
    readonly $controlPoints = input.required<{r: Vector2Like[]; g: Vector2Like[]; b: Vector2Like[]; rgb: Vector2Like[]}>({alias: "controlPoints"})
    readonly $selectedCurveType = signal<CurveType>("rgb")
    readonly $splineControlPoints = computed(() => {
        const curveType = this.$selectedCurveType()
        const controlPoints = this.$controlPoints()
        return controlPoints[curveType]
    })
    readonly $splineCurvePoints = computed(() =>
        evaluateRgbCurveOverRange(0, 1, 0.01, this.$splineControlPoints()).map((point) => ({
            position: point,
        })),
    )
    readonly $selectedSplineControlPoint = signal<number | undefined>(undefined)

    readonly $width = input(100, {alias: "width"})
    readonly $height = input(100, {alias: "height"})

    readonly onControlPointAdded = output<{curve: CurveType; point: Vector2Like}>()
    readonly onControlPointRemoved = output<{curve: CurveType; pointId: number}>()
    readonly onBeginControlPointDrag = output<void>()
    readonly onControlPointChanged = output<{curve: CurveType; pointId: number; point: Vector2Like}>()
    readonly onEndControlPointDrag = output<void>()
}
